/**
 *
 * Accessibility layer for Rebuy carousel widgets. Runs inside `rebuy.ready`
 * so Rebuy's Vue app is mounted before we touch the DOM.
 *
 * WHAT IT DOES
 * ------------
 * - Slide visibility:  Sets aria-hidden="true/false" and role="group" on each
 *   .rebuy-product-block slide, and labels them "slide X of Y".
 *   Slides count is scoped per .flickity-slider so two widgets on the same
 *   page each get their own accurate "1 of N" total.
 *
 * - Focus management: Removes focusable elements from tab order when their
 *   slide is hidden (saves the original tabindex in data-original-tabindex
 *   and restores it when the slide becomes active again).
 *
 * - Page dots:        Adds role="button", tabindex="0", Enter/Space keydown
 *   support, and aria-current="true" on the selected dot. Synced on every
 *   Flickity slide change via MutationObserver.
 *
 * - Carousel region: Sets role="region" and aria-label="carousel" on each
 *   .rebuy-product-grid. A separate MutationObserver re-applies role="region"
 *   if Rebuy's framework resets it to role="".
 *
 * - Price spans:      Strips erroneous tabindex="0" from .rebuy-money spans.
 *
 * TWO WIDGET INSTANCES
 * --------------------
 * This site has two Rebuy YMAL carousels:
 *   - PDP widget   (widget-type-product, id="rebuy-widget-63148")  — present
 *     in the DOM on page load, Flickity initializes synchronously.
 *   - Cart widget  (widget-type-cart,    id="rebuy-widget-131191") — embedded
 *     and already is-visible on load, but Flickity initializes async AFTER
 *     rebuy.ready fires. An is-visible MutationObserver won't catch this.
 *
 * Both are handled by watching every [data-rebuy-id] container for the moment
 * Flickity adds the `flickity-enabled` class to a .rebuy-product-grid. The
 * `data-a11y-init` flag prevents double-initialization on subsequent triggers.
 */
/* Normalise aria-label on subscription upgrade buttons in the cart flyout.
   Rebuy generates "Switch [product name] to a Subscription" which is verbose
   and redundant — each button is already within a labelled cart item. */

const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

/* Remove unnecessary tabindex from price spans */
function initPriceSpans() {
  const spans = document.querySelectorAll('.rebuy-money > span[tabindex="0"]');
  if (!spans.length) return;
  spans.forEach(span => span.removeAttribute('tabindex'));
}

/* Sync aria-current on page dots within a given carousel container */
function syncDots(container) {
  if (!container) return;
  const dots = container.querySelectorAll('.flickity-page-dots .dot');
  if (!dots.length) return;
  dots.forEach(dot => {
    if (dot.classList.contains('is-selected')) {
      dot.setAttribute('aria-current', 'true');
    } else {
      dot.removeAttribute('aria-current');
    }
  });
}

/* Initialize a11y attributes on page dots within a given carousel container */
function initDots(container) {
  if (!container) return;
  const dots = container.querySelectorAll('.flickity-page-dots .dot');
  if (!dots.length) return;
  dots.forEach(dot => {
    dot.setAttribute('role', 'button');
    dot.setAttribute('tabindex', '0');

    if (!dot._rebuyA11yKeydown) {
      dot._rebuyA11yKeydown = true;
      dot.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          dot.click();
        }
      });
    }
  });
  syncDots(container);
}

/* Hide off-screen slides from AT and manage focus — scoped to one carousel */
function updateSlideAccessibility(carousel) {
  if (!carousel) return;
  const slider = carousel.querySelector('.flickity-slider');
  if (!slider) return;

  const slides = slider.querySelectorAll(':scope > .rebuy-product-block');
  if (!slides.length) return;

  const totalSlides = slides.length;

  slides.forEach((slide, index) => {
    const isActive = slide.classList.contains('is-selected');

    slide.setAttribute('aria-hidden', String(!isActive));
    slide.setAttribute('role', 'group');
    slide.setAttribute('aria-label', `product ${index + 1} of ${totalSlides}`);

    const focusable = slide.querySelectorAll(FOCUSABLE);
    if (!focusable.length) return;
    focusable.forEach(el => {
      if (!isActive) {
        if (!el.hasAttribute('data-original-tabindex')) {
          const current = el.getAttribute('tabindex');
          el.setAttribute(
            'data-original-tabindex',
            current !== null ? current : 'none'
          );
        }
        el.setAttribute('tabindex', '-1');
      } else if (el.hasAttribute('data-original-tabindex')) {
        // Always remove tabindex on active slides — Rebuy sets tabindex="-1"
        // on image links by default, which we override to keep them reachable.
        el.removeAttribute('tabindex');
        el.removeAttribute('data-original-tabindex');
      } else if (el.getAttribute('tabindex') === '-1') {
        // Active slide element with Rebuy's native tabindex="-1" that was
        // never hidden by us — remove it so the element is keyboard-reachable.
        el.removeAttribute('tabindex');
      }
    });
  });
}

/* Run updateSlideAccessibility across all currently present carousels */
function updateAllSlideAccessibility() {
  const carousels = document.querySelectorAll(
    '.rebuy-product-grid.flickity-enabled'
  );
  if (!carousels.length) return;
  carousels.forEach(updateSlideAccessibility);
}

/* Set up region role, observers, and dot a11y for a single carousel element */
function initCarousel(carousel) {
  if (!carousel) return;
  if (carousel.dataset.a11yInit) return;
  carousel.dataset.a11yInit = 'true';

  carousel.setAttribute('role', 'region');
  if (!carousel.getAttribute('aria-label')) {
    carousel.setAttribute('aria-label', 'carousel');
  }

  // Rebuy may reset role="" — re-apply when it does
  new MutationObserver(() => {
    if (carousel.getAttribute('role') !== 'region') {
      carousel.setAttribute('role', 'region');
    }
  }).observe(carousel, { attributes: true, attributeFilter: ['role'] });

  // Re-run slide a11y and dot sync on any Flickity class change
  const onChange = new MutationObserver(() => {
    updateSlideAccessibility(carousel);
    syncDots(carousel);
  });

  const slider = carousel.querySelector('.flickity-slider');
  if (slider) {
    onChange.observe(slider, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class']
    });
  }

  const dotsContainer = carousel.querySelector('.flickity-page-dots');
  if (dotsContainer) {
    onChange.observe(dotsContainer, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class']
    });
  }

  initDots(carousel);
}

/* Initialize all Flickity carousels currently present in the DOM */
function initCarouselsIn() {
  const carousels = document.querySelectorAll(
    '.rebuy-product-grid.flickity-enabled'
  );
  if (!carousels.length) return;
  carousels.forEach(initCarousel);
}

document.addEventListener('rebuy.ready', function (event) {
  // --- Boot ---
  applyQuantityLimits();
  updateAllSlideAccessibility();
  initPriceSpans();
  initCarouselsIn();
  observeCartBubbles();

  // Watch all rebuy widget containers for Flickity initializing async
  // (handles embedded cart widgets already visible on load, lazy widgets, etc.)
  const rebuyContainers = document.querySelectorAll('[data-rebuy-id]');
  if (!rebuyContainers.length) return;
  rebuyContainers.forEach(container => {
    new MutationObserver(() => {
      container
        .querySelectorAll(
          '.rebuy-product-grid.flickity-enabled:not([data-a11y-init])'
        )
        .forEach(carousel => {
          initCarousel(carousel);
          updateSlideAccessibility(carousel);
          initPriceSpans();
        });
    }).observe(container, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class']
    });
  });
});

/* Rebuy generates verbose aria-labels like "Switch [product name] to a
   Subscription" on upgrade buttons. Since each button already lives inside
   a labelled cart item group, we replace it with the shorter, consistent
   label "Upgrade To Subscription". */
function replaceCartItemSubscriptionButtonLabels() {
  const subscriptionButtons = document.querySelectorAll(
    '.rebuy-cart__flyout-item-subscription button[aria-label]'
  );
  if (!subscriptionButtons.length) return;
  subscriptionButtons.forEach(button => {
    button.setAttribute('aria-label', 'Upgrade To Subscription');
  });
}
/* Rebuy renders the cart item list as <ul role="group" tabindex="0">.
   The role is redundant — <ul> is already a list — and tabindex="0" adds
   an extra, unhelpful tab stop. Both attributes are removed so AT reads
   it as a plain list. */
function removeCartParentUlAriaLabel() {
  const cartList = document.querySelectorAll('ul[data-smartcart-items]');
  if (!cartList.length) return;
  cartList.forEach(ul => {
    ul.removeAttribute('role');
    ul.removeAttribute('tabindex');
  });
}

function addCartBubbleAccessibility() {
  const bubbles = document.querySelectorAll('.cart-link__bubble');
  if (!bubbles.length) return;
  bubbles.forEach(bubble => {
    bubble.setAttribute('aria-hidden', 'true');
    const count = (bubble.innerText || bubble.textContent || '').trim();

    // Reuse existing sr-only sibling if present, otherwise create one
    let srText = bubble.parentNode.querySelector(':scope > .sr-only');
    if (!srText) {
      srText = document.createElement('span');
      srText.className = 'sr-only';
      bubble.insertAdjacentElement('afterend', srText);
    }
    srText.textContent = `${count} item${count === '1' ? '' : 's'} in cart`;
  });
}

function updateCartBubbleVisibility() {
  const bubbles = document.querySelectorAll('.cart-link__bubble');
  if (!bubbles.length) return;
  bubbles.forEach(bubble => {
    const count = (bubble.innerText || bubble.textContent || '').trim();
    if (count === '0' || count === '') {
      bubble.classList.remove('cart-link__bubble--visible');
    } else {
      bubble.classList.add('cart-link__bubble--visible');
    }
  });
}

/* Attach a MutationObserver to the cart bubble so aria-label stays in sync
   whenever Rebuy's Vue updates the count. Handles two timing issues:
     1. Bubble not yet in DOM when rebuy.ready fires (Rebuy renders async).
     2. Bubble text is updated after rebuy:cart.change fires, so reading
        textContent in that handler gives stale data. */
function observeCartBubbles() {
  function attachBubbleObserver(bubble) {
    addCartBubbleAccessibility();
    updateCartBubbleVisibility();
    new MutationObserver(() => {
      addCartBubbleAccessibility();
      updateCartBubbleVisibility();
    }).observe(bubble, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  const bubbles = document.querySelectorAll(
    '.rebuy-cart__flyout .cart-link__bubble'
  );
  if (bubbles.length) {
    bubbles.forEach(bubble => attachBubbleObserver(bubble));
    return;
  }

  // Bubble not yet in DOM — wait for Rebuy to render the cart flyout
  const waitObserver = new MutationObserver(() => {
    const b = document.querySelectorAll('.cart-link__bubble');
    if (!b.length) return;
    waitObserver.disconnect();
    b.forEach(bubble => attachBubbleObserver(bubble));
  });
  waitObserver.observe(document.body, { childList: true, subtree: true });
}

/* When a cart item reaches its quantity_limit metafield, natively disable the
   "+" (increase) button so the user can't exceed the limit. The "−" button
   stays active. Rebuy dims a disabled button automatically, so no CSS is
   needed. Idempotent — re-enables "+" when quantity drops below the limit.
   Items are matched to <li> elements via the "product-{handle}" class Rebuy
   renders on each <li>, rather than DOM order, so a stray/reordered <li>
   can't misalign the pairing.

   Also clamps quantity back down to the limit if it's ever exceeded (e.g. an
   item added over the limit from elsewhere, like a recommendation widget).
   Cart.setItemQuantity triggers its own rebuy:cart.change, which re-runs this
   function — the second pass sees quantity === limit, so it's a no-op and
   the recursion stops there. */
function applyQuantityLimits() {
  setTimeout(() => {
    const items = Rebuy.SmartCart.items();
    if (!items.length) return;

    const cartList = document.querySelector('ul[data-smartcart-items]');
    if (!cartList) return;

    items.forEach(item => {
      const handle = item.product?.handle;
      if (!handle) return;

      const li = cartList.querySelector(`:scope > li.product-${handle}`);
      if (!li) return;

      const limit = parseInt(
        item.product?.metafields?.custom?.quantity_limit,
        10
      );

      if (limit > 0 && item.quantity > limit) {
        Rebuy.Cart.setItemQuantity(item, limit);
        return;
      }

      const buttons = li.querySelectorAll(
        '.rebuy-cart__flyout-item-quantity-widget-button'
      );
      const plusBtn = Array.from(buttons).find(btn =>
        (btn.getAttribute('aria-label') || '').startsWith('Increase')
      );
      if (!plusBtn) return;

      plusBtn.disabled = limit > 0 && item.quantity >= limit;
    });
  }, 200);
}

/* Wrap each cart item's content in a div that carries the
   rebuy-cart__flyout-item class and group semantics. This keeps list
   structure on <li> while moving item-level semantics to the inner wrapper. */
function replaceCartItemRoles() {
  const cartList = document.querySelector('ul[data-smartcart-items]');
  if (!cartList) return;
  const items = cartList.querySelectorAll(':scope > li');
  if (!items.length) return;
  items.forEach(li => {
    const existingWrapper = li.querySelector(
      ':scope > .rebuy-cart__flyout-item'
    );

    li.classList.remove('rebuy-cart__flyout-item');
    li.removeAttribute('role');
    li.removeAttribute('aria-label');

    if (existingWrapper) {
      existingWrapper.setAttribute('role', 'group');
      existingWrapper.setAttribute('aria-label', 'product');
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'rebuy-cart__flyout-item';
    wrapper.setAttribute('role', 'group');
    wrapper.setAttribute('aria-label', 'product');

    while (li.firstChild) {
      wrapper.appendChild(li.firstChild);
    }

    li.appendChild(wrapper);
  });
}

// Re-apply subscription button labels whenever the Rebuy cart changes
// (items added, removed, or quantities updated inject new buttons into the DOM)
document.addEventListener('rebuy:cart.change', e => {
  applyQuantityLimits();
  replaceCartItemSubscriptionButtonLabels();
  removeCartParentUlAriaLabel();
  replaceCartItemRoles();
});
